import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { UpgradeBanner } from '../components/UpgradeModal';
import QuotaExhaustedPopup from '../components/QuotaExhaustedPopup';
import { ShareButton } from '../components/ShareCard';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Star, Sun, Moon, Sparkles, Download, Loader2, 
  Calendar, Clock, MapPin, User, ChevronRight, FileText, Crown, Lock, X, Share2,
  Search, Check
} from 'lucide-react';

import { BACKEND_URL } from '../lib/backend';

const API_URL = BACKEND_URL;

// Zodiac signs with symbols
const ZODIAC_SIGNS = [
  { name: 'Aries', symbol: 'Ar', hindi: 'मेष', fullSymbol: '♈' },
  { name: 'Taurus', symbol: 'Ta', hindi: 'वृषभ', fullSymbol: '♉' },
  { name: 'Gemini', symbol: 'Ge', hindi: 'मिथुन', fullSymbol: '♊' },
  { name: 'Cancer', symbol: 'Cn', hindi: 'कर्क', fullSymbol: '♋' },
  { name: 'Leo', symbol: 'Le', hindi: 'सिंह', fullSymbol: '♌' },
  { name: 'Virgo', symbol: 'Vi', hindi: 'कन्या', fullSymbol: '♍' },
  { name: 'Libra', symbol: 'Li', hindi: 'तुला', fullSymbol: '♎' },
  { name: 'Scorpio', symbol: 'Sc', hindi: 'वृश्चिक', fullSymbol: '♏' },
  { name: 'Sagittarius', symbol: 'Sg', hindi: 'धनु', fullSymbol: '♐' },
  { name: 'Capricorn', symbol: 'Cp', hindi: 'मकर', fullSymbol: '♑' },
  { name: 'Aquarius', symbol: 'Aq', hindi: 'कुंभ', fullSymbol: '♒' },
  { name: 'Pisces', symbol: 'Pi', hindi: 'मीन', fullSymbol: '♓' }
];

// Planet abbreviations for display
const PLANET_ABBREV = {
  'Sun': 'Su', 'Moon': 'Mo', 'Mars': 'Ma', 'Mercury': 'Me',
  'Jupiter': 'Ju', 'Venus': 'Ve', 'Saturn': 'Sa', 'Rahu': 'Ra', 'Ketu': 'Ke',
  'Surya': 'Su', 'Chandra': 'Mo', 'Mangal': 'Ma', 'Budha': 'Me',
  'Guru': 'Ju', 'Shukra': 'Ve', 'Shani': 'Sa'
};

// Navagraha planets with colors
const PLANETS = [
  { name: 'Sun', sanskrit: 'Surya', symbol: '☉', abbr: 'Su', color: '#FF6B35' },
  { name: 'Moon', sanskrit: 'Chandra', symbol: '☽', abbr: 'Mo', color: '#E8E8E8' },
  { name: 'Mars', sanskrit: 'Mangal', symbol: '♂', abbr: 'Ma', color: '#FF4444' },
  { name: 'Mercury', sanskrit: 'Budha', symbol: '☿', abbr: 'Me', color: '#4CAF50' },
  { name: 'Jupiter', sanskrit: 'Guru', symbol: '♃', abbr: 'Ju', color: '#FFB347' },
  { name: 'Venus', sanskrit: 'Shukra', symbol: '♀', abbr: 'Ve', color: '#FF69B4' },
  { name: 'Saturn', sanskrit: 'Shani', symbol: '♄', abbr: 'Sa', color: '#9C27B0' },
  { name: 'Rahu', sanskrit: 'Rahu', symbol: '☊', abbr: 'Ra', color: '#607D8B' },
  { name: 'Ketu', sanskrit: 'Ketu', symbol: '☋', abbr: 'Ke', color: '#795548' }
];

// Traditional North Indian Kundli Chart Component
const NorthIndianKundliChart = ({ houses, title, language }) => {
  if (!houses) return null;

  // Get planets for a specific house
  const getPlanetsForHouse = (houseNum) => {
    const house = houses[houseNum];
    if (!house?.planets || house.planets.length === 0) return [];
    return house.planets.map(p => {
      const abbr = PLANET_ABBREV[p] || p.substring(0, 2);
      return abbr;
    });
  };

  // Get sign abbreviation for a house
  const getSignForHouse = (houseNum) => {
    const house = houses[houseNum];
    if (!house?.sign) return '';
    const signIndex = ZODIAC_SIGNS.findIndex(z => 
      z.name.toLowerCase() === house.sign.toLowerCase() ||
      z.hindi === house.sign
    );
    return signIndex >= 0 ? (signIndex + 1) : '';
  };

  // House positions in North Indian diamond layout
  // Each house has: polygon points, text position (x, y)
  const houseLayout = {
    1:  { points: '150,10 220,80 150,150 80,80', textX: 150, textY: 70, planetsY: 90 },
    2:  { points: '80,80 150,10 10,10 10,80', textX: 55, textY: 45, planetsY: 60 },
    3:  { points: '10,80 80,80 80,150 10,150', textX: 45, textY: 115, planetsY: 130 },
    4:  { points: '10,150 80,150 150,220 10,220', textX: 55, textY: 185, planetsY: 200 },
    5:  { points: '10,220 150,220 80,290 10,290', textX: 55, textY: 255, planetsY: 270 },
    6:  { points: '80,290 150,220 150,290', textX: 115, textY: 270, planetsY: 282 },
    7:  { points: '150,150 220,220 150,290 80,220', textX: 150, textY: 230, planetsY: 250 },
    8:  { points: '150,220 150,290 220,290', textX: 185, textY: 270, planetsY: 282 },
    9:  { points: '220,220 290,220 290,290 150,290', textX: 245, textY: 255, planetsY: 270 },
    10: { points: '220,150 290,150 290,220 150,220', textX: 245, textY: 185, planetsY: 200 },
    11: { points: '220,80 290,80 290,150 220,150', textX: 255, textY: 115, planetsY: 130 },
    12: { points: '150,10 290,10 290,80 220,80', textX: 245, textY: 45, planetsY: 60 }
  };

  return (
    <div className="w-full max-w-[320px] mx-auto">
      {/* Title */}
      <div className="text-center mb-4">
        <h3 className="text-[#C84B32] font-medium text-base">{title}</h3>
      </div>
      
      {/* Chart */}
      <div className="relative bg-gradient-to-br from-white to-[#FCE9D9] rounded-2xl p-4 border border-[#C84B32]/20 shadow-xl">
        <svg viewBox="0 0 300 300" className="w-full h-auto">
          {/* Background gradient */}
          <defs>
            <linearGradient id="chartBg" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1a0f2e" />
              <stop offset="100%" stopColor="#0d0618" />
            </linearGradient>
            <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#F3E5AB" />
            </linearGradient>
          </defs>

          {/* Outer border - Diamond shape */}
          <polygon 
            points="150,10 290,150 150,290 10,150" 
            fill="none" 
            stroke="url(#goldGradient)" 
            strokeWidth="2"
          />
          
          {/* Inner rectangle */}
          <rect 
            x="80" y="80" 
            width="140" height="140" 
            fill="none" 
            stroke="#D4AF37" 
            strokeWidth="1.5"
            opacity="0.8"
          />

          {/* Diagonal lines from corners to center rectangle */}
          <line x1="150" y1="10" x2="80" y2="80" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />
          <line x1="150" y1="10" x2="220" y2="80" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />
          <line x1="290" y1="150" x2="220" y2="80" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />
          <line x1="290" y1="150" x2="220" y2="220" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />
          <line x1="150" y1="290" x2="220" y2="220" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />
          <line x1="150" y1="290" x2="80" y2="220" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />
          <line x1="10" y1="150" x2="80" y2="220" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />
          <line x1="10" y1="150" x2="80" y2="80" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />

          {/* Center diagonals */}
          <line x1="80" y1="80" x2="220" y2="220" stroke="#D4AF37" strokeWidth="1" opacity="0.4" />
          <line x1="220" y1="80" x2="80" y2="220" stroke="#D4AF37" strokeWidth="1" opacity="0.4" />

          {/* House content */}
          {Object.entries(houseLayout).map(([houseNum, layout]) => {
            const planets = getPlanetsForHouse(parseInt(houseNum));
            const signNum = getSignForHouse(parseInt(houseNum));
            
            return (
              <g key={houseNum}>
                {/* House number (small, top-left of area) */}
                <text 
                  x={layout.textX - 15} 
                  y={layout.textY - 8} 
                  fill="#6B7280" 
                  fontSize="8" 
                  textAnchor="middle"
                  fontFamily="monospace"
                >
                  {houseNum}
                </text>
                
                {/* Sign number */}
                <text 
                  x={layout.textX} 
                  y={layout.textY} 
                  fill="#D4AF37" 
                  fontSize="14" 
                  fontWeight="bold"
                  textAnchor="middle"
                  fontFamily="serif"
                >
                  {signNum}
                </text>
                
                {/* Planets in this house */}
                {planets.length > 0 && (
                  <text 
                    x={layout.textX} 
                    y={layout.planetsY} 
                    fill="#F3EFE0" 
                    fontSize="10" 
                    textAnchor="middle"
                    fontFamily="system-ui"
                  >
                    {planets.join(' ')}
                  </text>
                )}
              </g>
            );
          })}

          {/* Lagna indicator in center */}
          <text 
            x="150" 
            y="155" 
            fill="#D4AF37" 
            fontSize="12" 
            textAnchor="middle"
            fontFamily="serif"
            fontStyle="italic"
          >
            {language === 'hi' ? 'लग्न' : 'Asc'}
          </text>
        </svg>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap justify-center gap-3 text-xs">
        <span className="text-[#8A8279]">
          <span className="text-[#C84B32]">1-12</span> = {language === 'hi' ? 'राशि' : 'Sign'}
        </span>
        <span className="text-[#8A8279]">
          <span className="text-[#282622]">Su Mo Ma</span> = {language === 'hi' ? 'ग्रह' : 'Planets'}
        </span>
      </div>
    </div>
  );
};

// Location Search Component with Autocomplete
const LocationPicker = ({ value, onChange, language }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Common Indian cities for quick selection
  const popularCities = [
    { name: 'Delhi', full: 'New Delhi, India', lat: 28.6139, lng: 77.2090 },
    { name: 'Mumbai', full: 'Mumbai, Maharashtra, India', lat: 19.0760, lng: 72.8777 },
    { name: 'Bangalore', full: 'Bangalore, Karnataka, India', lat: 12.9716, lng: 77.5946 },
    { name: 'Chennai', full: 'Chennai, Tamil Nadu, India', lat: 13.0827, lng: 80.2707 },
    { name: 'Kolkata', full: 'Kolkata, West Bengal, India', lat: 22.5726, lng: 88.3639 },
    { name: 'Hyderabad', full: 'Hyderabad, Telangana, India', lat: 17.3850, lng: 78.4867 },
    { name: 'Pune', full: 'Pune, Maharashtra, India', lat: 18.5204, lng: 73.8567 },
    { name: 'Jaipur', full: 'Jaipur, Rajasthan, India', lat: 26.9124, lng: 75.7873 },
    { name: 'Lucknow', full: 'Lucknow, Uttar Pradesh, India', lat: 26.8467, lng: 80.9462 },
    { name: 'Ahmedabad', full: 'Ahmedabad, Gujarat, India', lat: 23.0225, lng: 72.5714 },
    { name: 'Varanasi', full: 'Varanasi, Uttar Pradesh, India', lat: 25.3176, lng: 82.9739 },
    { name: 'Indore', full: 'Indore, Madhya Pradesh, India', lat: 22.7196, lng: 75.8577 }
  ];

  // Search for locations using backend API
  const searchLocations = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/location/search?q=${encodeURIComponent(searchQuery)}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.locations || []);
      } else {
        // Fallback to filtering popular cities
        const filtered = popularCities.filter(city => 
          city.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          city.full.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setSuggestions(filtered.map(c => ({ name: c.full, lat: c.lat, lng: c.lng })));
      }
    } catch (err) {
      // Fallback to popular cities
      const filtered = popularCities.filter(city => 
        city.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        city.full.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSuggestions(filtered.map(c => ({ name: c.full, lat: c.lat, lng: c.lng })));
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query) {
        searchLocations(query);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectLocation = (location) => {
    const locationString = location.name || location.full;
    setQuery(locationString);
    onChange(locationString, location.lat, location.lng);
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C84B32]" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder={language === 'hi' ? 'जन्म स्थान खोजें...' : 'Search birth place...'}
          className="w-full bg-white border border-[#282622]/10 text-[#282622] focus:border-[#C84B32] h-12 rounded-xl pl-10 pr-10 outline-none transition-colors"
          data-testid="location-search-input"
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C84B32] animate-spin" />
        )}
        {!isSearching && query && (
          <button 
            onClick={() => {
              setQuery('');
              onChange('');
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8279] hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {showDropdown && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-2 bg-white border border-[#282622]/10 rounded-xl shadow-xl overflow-hidden max-h-64 overflow-y-auto"
          >
            {/* Popular cities */}
            {!query && (
              <div className="p-3 border-b border-[#282622]/10">
                <p className="text-[#8A8279] text-xs mb-2 px-2">
                  {language === 'hi' ? 'लोकप्रिय शहर' : 'Popular Cities'}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {popularCities.slice(0, 6).map((city) => (
                    <button
                      key={city.name}
                      onClick={() => selectLocation(city)}
                      className="text-xs px-3 py-2 rounded-lg bg-white text-[#6B6560] hover:bg-[#C84B32]/10 hover:text-[#C84B32] transition-colors text-left truncate"
                    >
                      {city.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Search results */}
            {suggestions.length > 0 && (
              <div className="py-2">
                {suggestions.map((location, i) => (
                  <button
                    key={i}
                    onClick={() => selectLocation(location)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#C84B32]/10 transition-colors text-left"
                  >
                    <MapPin className="w-4 h-4 text-[#C84B32] flex-shrink-0" />
                    <span className="text-[#282622] text-sm truncate">{location.name}</span>
                  </button>
                ))}
              </div>
            )}

            {/* No results */}
            {query && suggestions.length === 0 && !isSearching && (
              <div className="p-4 text-center text-[#8A8279] text-sm">
                {language === 'hi' ? 'कोई स्थान नहीं मिला' : 'No locations found'}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const JanamKundliPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { checkAccess, isPremium } = useFeatureAccess();
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [kundliData, setKundliData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [error, setError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [featureLocked, setFeatureLocked] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profiles`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProfiles(data);
        if (data.length > 0) {
          setSelectedProfile(data[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching profiles:', err);
    }
  };

  const generateKundli = async () => {
    if (!selectedProfile) {
      setError(language === 'hi' ? 'कृपया पहले एक प्रोफ़ाइल चुनें' : 'Please select a profile first');
      return;
    }

    // Check feature access before generating
    if (!isPremium) {
      const access = await checkAccess('kundli');
      if (!access.allowed) {
        setFeatureLocked(true);
        setShowUpgradeModal(true);
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/kundli/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ profile_id: selectedProfile.profile_id })
      });

      if (response.ok) {
        const data = await response.json();
        setKundliData(data);
      } else if (response.status === 403) {
        setFeatureLocked(true);
        setShowUpgradeModal(true);
      } else {
        const err = await response.json();
        setError(err.detail || (language === 'hi' ? 'कुंडली बनाने में विफल' : 'Failed to generate Kundli'));
      }
    } catch (err) {
      setError(language === 'hi' ? 'कुंडली बनाने में विफल। कृपया पुनः प्रयास करें।' : 'Failed to generate Kundli. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!kundliData || !selectedProfile) return;

    setGeneratingPdf(true);
    try {
      const response = await fetch(`${API_URL}/api/kundli/download-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          profile_id: selectedProfile.profile_id,
          kundli_data: kundliData 
        })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Janam_Kundli_${selectedProfile.name.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        setError(language === 'hi' ? 'PDF बनाने में विफल' : 'Failed to generate PDF');
      }
    } catch (err) {
      console.error('PDF download error:', err);
      setError(language === 'hi' ? 'PDF डाउनलोड करने में विफल' : 'Failed to download PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9EF] text-white">
      {/* Background effects */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(120,80,200,0.15),transparent)]" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FFF9EF] border-b border-[#282622]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-lg hover:bg-[#FCE9D9]/60 transition-colors"
                data-testid="kundli-back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-[#6B6560]" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C84B32]/20 to-[#F5B942]/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#C84B32]" />
                </div>
                <div>
                  <h1 className="font-['Fraunces'] text-lg sm:text-xl text-[#282622]">
                    {language === 'hi' ? 'जन्म कुंडली' : 'Birth Chart'}
                  </h1>
                  <p className="text-[#8A8279] text-xs">
                    {language === 'hi' ? 'वैदिक ज्योतिष चार्ट' : 'Vedic Astrology Chart'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Selector */}
        <section className="mb-8">
          <h2 className="font-['Fraunces'] text-xl text-[#282622] mb-4">
            {language === 'hi' ? 'प्रोफाइल चुनें' : 'Select Profile'}
          </h2>
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
            {profiles.map((profile) => (
              <motion.button
                key={profile.profile_id}
                onClick={() => {
                  setSelectedProfile(profile);
                  setKundliData(null);
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid={`kundli-profile-${profile.profile_id}`}
                className={`flex-shrink-0 flex flex-col items-center gap-2 p-4 rounded-xl transition-all ${
                  selectedProfile?.profile_id === profile.profile_id
                    ? 'bg-gradient-to-br from-[#C84B32]/20 to-[#F5B942]/20 border-2 border-[#C84B32]'
                    : 'bg-[#FFF9EF] border-2 border-transparent hover:border-[#C84B32]/30'
                }`}
              >
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-semibold"
                  style={{ background: `linear-gradient(135deg, ${profile.avatar_color || '#D4AF37'}, #6A0DAD)` }}
                >
                  <span className="text-white">{profile.name[0]}</span>
                </div>
                <span className={`text-sm font-medium ${
                  selectedProfile?.profile_id === profile.profile_id ? 'text-[#C84B32]' : 'text-[#6B6560]'
                }`}>
                  {profile.name}
                </span>
              </motion.button>
            ))}
            
            <motion.button
              onClick={() => navigate('/profiles')}
              whileHover={{ scale: 1.02 }}
              className="flex-shrink-0 flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-[#FFF9EF] border-2 border-dashed border-[#282622]/10 hover:border-[#C84B32]/50 transition-all"
            >
              <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center border border-[#282622]/10">
                <User className="w-6 h-6 text-[#6B6560]" />
              </div>
              <span className="text-sm text-[#6B6560]">{language === 'hi' ? 'नया जोड़ें' : 'Add New'}</span>
            </motion.button>
          </div>
        </section>

        {/* Selected Profile Details */}
        {selectedProfile && (
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="bg-gradient-to-br from-white to-[#FCE9D9] rounded-2xl border border-[#282622]/10 p-6">
              <h3 className="font-['Fraunces'] text-lg text-[#282622] mb-4">
                {language === 'hi' ? 'जन्म विवरण' : 'Birth Details'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-[#FFF9EF] border border-[#282622]/10">
                  <Calendar className="w-5 h-5 text-[#C84B32]" />
                  <div>
                    <p className="text-[#8A8279] text-xs">{language === 'hi' ? 'जन्म तिथि' : 'Date of Birth'}</p>
                    <p className="text-[#282622] text-sm font-medium">{selectedProfile.date_of_birth}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-[#FFF9EF] border border-[#282622]/10">
                  <Clock className="w-5 h-5 text-[#C84B32]" />
                  <div>
                    <p className="text-[#8A8279] text-xs">{language === 'hi' ? 'जन्म समय' : 'Time of Birth'}</p>
                    <p className="text-[#282622] text-sm font-medium">
                      {selectedProfile.time_of_birth || (language === 'hi' ? 'निर्दिष्ट नहीं' : 'Not specified')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-[#FFF9EF] border border-[#282622]/10">
                  <MapPin className="w-5 h-5 text-[#C84B32]" />
                  <div>
                    <p className="text-[#8A8279] text-xs">{language === 'hi' ? 'जन्म स्थान' : 'Place of Birth'}</p>
                    <p className="text-[#282622] text-sm font-medium">
                      {selectedProfile.place_of_birth || (language === 'hi' ? 'निर्दिष्ट नहीं' : 'Not specified')}
                    </p>
                  </div>
                </div>
              </div>
              
              {!kundliData && (
                <div className="mt-6 text-center">
                  <Button
                    onClick={generateKundli}
                    disabled={loading}
                    data-testid="generate-kundli-btn"
                    className="bg-gradient-to-r from-[#C84B32] to-[#E85D43] hover:from-[#E85D43] hover:to-[#F5B942] text-white px-8 py-3 rounded-xl font-semibold shadow-lg shadow-[#C84B32]/20"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {language === 'hi' ? 'बना रहे हैं...' : 'Generating...'}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        {language === 'hi' ? 'कुंडली बनाएं' : 'Generate Kundli'}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </motion.section>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Kundli Results */}
        <AnimatePresence>
          {kundliData && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Action Buttons */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Fraunces'] text-xl text-[#282622]">
                  {language === 'hi' ? 'आपकी जन्म कुंडली' : 'Your Birth Chart'}
                </h3>
                <div className="flex gap-3">
                  <ShareButton 
                    type="kundli"
                    data={{
                      sun_sign: kundliData.lagna_name,
                      moon_sign: kundliData.rashi_name,
                      nakshatra: kundliData.nakshatra,
                      lagna: kundliData.lagna_name
                    }}
                    userName={selectedProfile?.name}
                  />
                  <Button
                    onClick={downloadPdf}
                    disabled={generatingPdf}
                    data-testid="download-kundli-pdf"
                    className="bg-[#C84B32] hover:bg-[#E85D43] text-white px-4 py-2 rounded-xl font-medium"
                  >
                    {generatingPdf ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        {language === 'hi' ? 'PDF' : 'PDF'}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Charts Grid - D1 and D9 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* D-1 Lagna Chart */}
                <div className="bg-gradient-to-br from-white to-[#FCE9D9] rounded-2xl border border-[#282622]/10 p-6">
                  <NorthIndianKundliChart 
                    houses={kundliData.houses} 
                    title={language === 'hi' ? 'D-1 लग्न कुंडली' : 'D-1 Lagna Chart'}
                    language={language}
                  />
                  <p className="text-center text-[#8A8279] text-xs mt-4">
                    {language === 'hi' ? 'मुख्य जन्म कुंडली - जीवन का समग्र दृष्टिकोण' : 'Main Birth Chart - Overall life perspective'}
                  </p>
                </div>
                
                {/* D-9 Navamsa Chart */}
                {kundliData.navamsa && (
                  <div className="bg-gradient-to-br from-white to-[#FCE9D9] rounded-2xl border border-[#282622]/10 p-6">
                    <NorthIndianKundliChart 
                      houses={kundliData.navamsa.houses} 
                      title={language === 'hi' ? 'D-9 नवांश कुंडली' : 'D-9 Navamsa Chart'}
                      language={language}
                    />
                    <p className="text-center text-[#8A8279] text-xs mt-4">
                      {language === 'hi' ? 'विवाह और आध्यात्मिक क्षमता' : 'Marriage & spiritual potential'}
                    </p>
                  </div>
                )}
              </div>

              {/* Quick Info Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                <div className="bg-gradient-to-br from-white to-[#FCE9D9] rounded-xl border border-[#282622]/10 p-4 text-center">
                  <p className="text-[#8A8279] text-xs mb-1">{language === 'hi' ? 'लग्न' : 'Ascendant'}</p>
                  <p className="text-[#C84B32] font-semibold text-lg">
                    {ZODIAC_SIGNS[kundliData.lagna - 1]?.fullSymbol} {kundliData.lagna_name}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-white to-[#FCE9D9] rounded-xl border border-[#282622]/10 p-4 text-center">
                  <p className="text-[#8A8279] text-xs mb-1">{language === 'hi' ? 'चंद्र राशि' : 'Moon Sign'}</p>
                  <p className="text-[#C84B32] font-semibold text-lg">
                    {ZODIAC_SIGNS[kundliData.rashi - 1]?.fullSymbol} {kundliData.rashi_name}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-white to-[#FCE9D9] rounded-xl border border-[#282622]/10 p-4 text-center">
                  <p className="text-[#8A8279] text-xs mb-1">{language === 'hi' ? 'नक्षत्र' : 'Nakshatra'}</p>
                  <p className="text-[#C84B32] font-semibold text-lg">{kundliData.nakshatra}</p>
                </div>
                {kundliData.navamsa && (
                  <div className="bg-gradient-to-br from-white to-[#FCE9D9] rounded-xl border border-[#282622]/10 p-4 text-center">
                    <p className="text-[#8A8279] text-xs mb-1">{language === 'hi' ? 'नवांश लग्न' : 'Navamsa Lagna'}</p>
                    <p className="text-[#C84B32] font-semibold text-lg">{kundliData.navamsa.lagna}</p>
                  </div>
                )}
              </div>

              {/* Planetary Positions Table */}
              <div className="bg-gradient-to-br from-white to-[#FCE9D9] rounded-2xl border border-[#282622]/10 p-6 mb-8">
                <h4 className="font-['Fraunces'] text-lg text-[#282622] mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#C84B32]" />
                  {language === 'hi' ? 'ग्रह स्थिति' : 'Planetary Positions'}
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#282622]/10">
                        <th className="text-left py-3 px-4 text-[#8A8279] text-xs font-medium">{language === 'hi' ? 'ग्रह' : 'Planet'}</th>
                        <th className="text-left py-3 px-4 text-[#8A8279] text-xs font-medium">{language === 'hi' ? 'राशि' : 'Sign'}</th>
                        <th className="text-left py-3 px-4 text-[#8A8279] text-xs font-medium">{language === 'hi' ? 'भाव' : 'House'}</th>
                        <th className="text-left py-3 px-4 text-[#8A8279] text-xs font-medium">{language === 'hi' ? 'अंश' : 'Degree'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kundliData.planets?.map((planet, i) => {
                        const planetInfo = PLANETS.find(p => p.name === planet.name || p.sanskrit === planet.name);
                        return (
                          <tr key={i} className="border-b border-[#282622]/10 hover:bg-[#FCE9D9]/60 transition-colors">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span style={{ color: planetInfo?.color || '#D4AF37' }}>{planetInfo?.symbol || '★'}</span>
                                <span className="text-[#282622] text-sm">{planet.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-[#6B6560] text-sm">{planet.sign}</td>
                            <td className="py-3 px-4">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#C84B32]/10 text-[#C84B32] text-sm font-medium">
                                {planet.house}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-[#6B6560] text-sm">{planet.degree || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Dasha Information */}
              {kundliData.current_dasha && (
                <div className="bg-gradient-to-br from-white to-[#FCE9D9] rounded-2xl border border-[#282622]/10 p-6 mb-8">
                  <h4 className="font-['Fraunces'] text-lg text-[#282622] mb-4 flex items-center gap-2">
                    <Moon className="w-5 h-5 text-[#C84B32]" />
                    {language === 'hi' ? 'वर्तमान दशा' : 'Current Dasha Period'}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-[#FFF9EF] border border-[#282622]/10 text-center">
                      <p className="text-[#8A8279] text-xs mb-2">{language === 'hi' ? 'महादशा' : 'Maha Dasha'}</p>
                      <p className="text-[#C84B32] text-xl font-semibold">{kundliData.current_dasha.maha}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-[#FFF9EF] border border-[#282622]/10 text-center">
                      <p className="text-[#8A8279] text-xs mb-2">{language === 'hi' ? 'अंतर्दशा' : 'Antar Dasha'}</p>
                      <p className="text-[#C84B32] text-xl font-semibold">{kundliData.current_dasha.antar}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-[#FFF9EF] border border-[#282622]/10 text-center">
                      <p className="text-[#8A8279] text-xs mb-2">{language === 'hi' ? 'प्रत्यंतर दशा' : 'Pratyantar'}</p>
                      <p className="text-[#C84B32] text-xl font-semibold">{kundliData.current_dasha.pratyantar}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Interpretation */}
              {kundliData.interpretation && (
                <div className="bg-gradient-to-br from-white to-[#FCE9D9] rounded-2xl border border-[#C84B32]/20 p-6">
                  <h4 className="font-['Fraunces'] text-lg text-[#282622] mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#C84B32]" />
                    {language === 'hi' ? 'AI व्याख्या' : 'AI Interpretation'}
                  </h4>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <p className="text-[#6B6560] leading-relaxed whitespace-pre-line">
                      {kundliData.interpretation}
                    </p>
                  </div>
                </div>
              )}
            </motion.section>
          )}
        </AnimatePresence>

        {/* No Profile Message */}
        {profiles.length === 0 && (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto rounded-full bg-white flex items-center justify-center mb-4 border border-[#282622]/10">
              <User className="w-10 h-10 text-[#6B6560]" />
            </div>
            <h3 className="font-['Fraunces'] text-xl text-[#282622] mb-2">
              {language === 'hi' ? 'कोई प्रोफ़ाइल नहीं' : 'No Profiles Found'}
            </h3>
            <p className="text-[#6B6560] mb-6">
              {language === 'hi' ? 'अपनी जन्म कुंडली बनाने के लिए एक प्रोफ़ाइल बनाएं' : 'Create a profile to generate your Janam Kundli'}
            </p>
            <Button
              onClick={() => navigate('/profiles')}
              className="bg-gradient-to-r from-[#C84B32] to-[#E85D43] text-white px-6 py-3 rounded-xl font-medium"
            >
              {language === 'hi' ? 'प्रोफ़ाइल बनाएं' : 'Create Profile'}
            </Button>
          </div>
        )}
      </main>
      
      {/* Upgrade Modal */}
      <QuotaExhaustedPopup 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)}
        feature="kundli"
      />

      {/* Upgrade Banner */}
      {featureLocked && !showUpgradeModal && (
        <div className="fixed bottom-20 left-4 right-4 z-40">
          <UpgradeBanner 
            feature="kundli"
            onUpgradeClick={() => setShowUpgradeModal(true)}
          />
        </div>
      )}
    </div>
  );
};

export default JanamKundliPage;
